"use client";

import Image from "next/image";
import { useState } from "react";
import { CarFront, Check, ChevronDown, ImageIcon, Loader2 } from "lucide-react";
import { useVehicleUploads } from "@/hooks/use-vehicle-uploads";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";

interface VehicleHistoryProps {
  selectedUploadId: string | null;
  onSelect: (uploadId: string, imageUrl: string) => void;
  disabled?: boolean;
}

export function VehicleHistory({ selectedUploadId, onSelect, disabled = false }: VehicleHistoryProps) {
  const [open, setOpen] = useState(false);
  const { data, isLoading } = useVehicleUploads();

  const uploads = data?.uploads || [];
  const hasUploads = uploads.length > 0;

  if (!hasUploads && !isLoading) {
    return null;
  }

  const formatDate = (date: Date) => {
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return "Today";
    } else if (diffDays === 1) {
      return "Yesterday";
    } else if (diffDays < 7) {
      return `${diffDays}d ago`;
    } else {
      return date.toLocaleDateString("en-US", { 
        month: "short", 
        day: "numeric",
      });
    }
  };

  return (
    <Popover open={open && !disabled} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn(
            "gap-2 h-8 bg-white/5 border-white/10 transition-all",
            !disabled && "hover:bg-white/10 hover:border-white/20 text-zinc-300 hover:text-white",
            disabled && "opacity-50 cursor-not-allowed text-zinc-500"
          )}
          disabled={isLoading || !hasUploads || disabled}
        >
          <CarFront className="w-3.5 h-3.5 shrink-0" />
          {isLoading ? (
            <span className="flex items-center gap-1 text-xs">
              <Loader2 className="w-3 h-3 animate-spin" />
              Loading
            </span>
          ) : (
            <>
              <span className="text-xs font-medium whitespace-nowrap">My Garage ({uploads.length})</span>
              {!disabled && (
                <ChevronDown className={cn("w-3 h-3 shrink-0 opacity-60 transition-transform", open && "rotate-180")} />
              )}
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent 
        className="p-0 bg-zinc-900/95 backdrop-blur-xl border-white/10 shadow-2xl overflow-hidden"
        style={{ width: "340px", maxWidth: "calc(100vw - 32px)" }}
        align="end"
        side="bottom"
        sideOffset={8}
        collisionPadding={16}
      >
        {/* Header */}
        <div className="px-4 py-3 border-b border-white/10 bg-linear-to-r from-blue-500/10 to-transparent">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-blue-500/20 flex items-center justify-center">
              <CarFront className="w-3.5 h-3.5 text-blue-400" />
            </div>
            <div>
              <h4 className="font-semibold text-sm text-white">My Garage</h4>
              <p className="text-[11px] text-zinc-500">Select to use again</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <ScrollArea className="h-[320px]">
          <div className="p-2 space-y-1">
            {uploads.map((upload) => {
              const isSelected = selectedUploadId === upload.id;
              const generationCount = upload.generations?.length || 0;
              const uploadDate = formatDate(new Date(upload.createdAt));
              
              return (
                <button
                  key={upload.id}
                  onClick={() => {
                    if (upload.imageUrl) {
                      onSelect(upload.id, upload.imageUrl);
                      setOpen(false);
                    }
                  }}
                  disabled={!upload.imageUrl}
                  className={cn(
                    "group w-full flex items-center gap-3 p-2.5 rounded-lg text-left transition-all duration-200",
                    "hover:bg-white/5 border",
                    isSelected
                      ? "bg-blue-500/10 border-blue-500/30"
                      : "bg-transparent border-transparent hover:border-white/10",
                    !upload.imageUrl && "opacity-50 cursor-not-allowed"
                  )}
                >
                  {/* Thumbnail */}
                  <div className={cn(
                    "relative w-16 h-12 rounded-md overflow-hidden shrink-0 ring-1 transition-all duration-200",
                    isSelected 
                      ? "ring-blue-500/50" 
                      : "ring-white/10 group-hover:ring-white/20"
                  )}>
                    {upload.imageUrl ? (
                      <Image
                        src={upload.imageUrl}
                        alt="Car"
                        fill
                        sizes="64px"
                        className="object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-zinc-800/50">
                        <ImageIcon className="w-4 h-4 text-zinc-600" />
                      </div>
                    )}
                  </div>
                  
                  {/* Info - fixed width container */}
                  <div className="flex-1 min-w-0">
                    <p className={cn(
                      "text-[13px] font-medium leading-tight transition-colors",
                      isSelected ? "text-blue-400" : "text-white group-hover:text-blue-300"
                    )}>
                      {isSelected ? "Selected car" : "Saved car"}
                    </p>
                    <p className="text-[11px] text-zinc-500 mt-0.5">
                      {uploadDate} · {generationCount === 0 ? "Not used yet" : `${generationCount} preview${generationCount !== 1 ? "s" : ""}`}
                    </p>
                  </div>

                  {/* Selected indicator */}
                  <div className={cn(
                    "shrink-0 w-5 h-5 rounded-full flex items-center justify-center transition-all",
                    isSelected 
                      ? "bg-blue-500" 
                      : "bg-white/5 group-hover:bg-white/10"
                  )}>
                    {isSelected && (
                      <Check className="w-3 h-3 text-white" strokeWidth={3} />
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
